import type { DateRangePickerComponentProps } from "@quillsql/react"
import { ChangeEvent } from "react"
import { DateRange } from "react-day-picker"

import { faIcon } from "@utils/faIcon"
import formatDate from "@utils/formatDate"

import { Button, Label } from "@atoms"

import { DatePicker, Select } from "@molecules"
import type { SelectListItem } from "@molecules/Select/SelectListItem/SelectListItem.types"

import styles from "./QuillDateRangePicker.module.scss"

function formatDateLabel(date: Date): string {
    return formatDate({
        datetime: date,
        addYear: date.getFullYear() !== new Date().getFullYear(),
    }).localDateString
}

export default function QuillDateRangePicker(props: DateRangePickerComponentProps) {
    const { dateRange, label, presetOptions, preset, onChangeDateRange, onChangePreset } = props

    const currentRange: DateRange | undefined =
        dateRange.startDate && dateRange.endDate ? { from: dateRange.startDate, to: dateRange.endDate } : undefined

    const dateRangeLabel =
        currentRange?.from && currentRange?.to
            ? `${formatDateLabel(currentRange.from)} - ${formatDateLabel(currentRange.to)}`
            : "Select dates"

    const selectOptions: SelectListItem[] = presetOptions.map((option) => ({
        label: option.label,
        value: option.value,
    }))

    const currentPreset = selectOptions.find((option) => option.value === preset) ?? null

    const handleDateRangeSelect = (range: DateRange | undefined) => {
        if (range?.from && range?.to) {
            onChangeDateRange({ startDate: range.from, endDate: range.to })
        }
    }

    const handlePresetSelect = (item: SelectListItem) => {
        const syntheticEvent = {
            target: { value: item.value },
        } as ChangeEvent<HTMLSelectElement>
        onChangePreset(syntheticEvent)
    }

    return (
        <div className={styles.base}>
            {label && <Label label={label} variant="subtle" />}
            <div className={styles.inputGroup}>
                <DatePicker mode="range" selected={currentRange} onSelect={handleDateRangeSelect} numberOfMonths={2}>
                    <Button
                        variant="subtle"
                        size="md"
                        leftIcon={faIcon({ name: "calendar", style: "regular" })}
                        className={styles.dateButton}
                    >
                        {dateRangeLabel}
                    </Button>
                </DatePicker>
                {selectOptions.length > 0 && (
                    <Select.Root defaultSelectedItem={currentPreset}>
                        <Select.Input
                            isControlled={false}
                            placeholder="Preset"
                            size="sm"
                            isGrouped={true}
                            groupPosition="end"
                        />
                        <Select.Viewport>
                            <Select.List title={label ?? "Date range"}>
                                <Select.ListGroup>
                                    {selectOptions.map((option) => (
                                        <Select.ListItem
                                            key={option.value}
                                            variant="default"
                                            item={option}
                                            onSelect={handlePresetSelect}
                                        />
                                    ))}
                                </Select.ListGroup>
                            </Select.List>
                        </Select.Viewport>
                    </Select.Root>
                )}
            </div>
        </div>
    )
}
